import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Sparkles, Send, X, Maximize2, Minimize2,
    Loader2, MessageCircle, ChevronDown, ChevronUp,
    Download, FileText, FileSpreadsheet, BarChart3, FileType, Printer
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

// ═══════════════════════════════════════════════════════════
//  ANALYTICS AI PANEL
//  Embeddable, collapsible, expandable AI chat for analytics
// ═══════════════════════════════════════════════════════════

const TOKENS = {
    accent: '#6366f1',
    accentLight: '#818cf8',
    accentBg: '#EEF2FF',
    navy: '#2A3F54',
    navyLight: '#3A5068',
    textPrimary: '#202938',
    textSecondary: '#626B7A',
    textMuted: '#8A8F98',
    border: '#E5E1DD',
    borderLight: '#EDEBE9',
    bgPage: '#FAFAF9',
    bgCard: '#FFFFFF',
    bgHover: '#F2EFED',
};

const QUICK_SUGGESTIONS = [
    "What areas need the most attention?",
    "Show me employees at risk of falling behind",
    "What are the biggest completion bottlenecks?",
    "Summarize the key trends",
];

const AnalyticsAIPanel = ({
    analyticsContext = {},   // { type, id, title, data }
    defaultExpanded = false,
}) => {
    const [isCollapsed, setIsCollapsed] = useState(!defaultExpanded);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);
    const [reportPreview, setReportPreview] = useState(null); // { title, markdown }
    const [reportType, setReportType] = useState('comprehensive');
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // scroll chat to bottom on new messages
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // focus input when expanded
    useEffect(() => {
        if (!isCollapsed) {
            setTimeout(() => inputRef.current?.focus(), 200);
        }
    }, [isCollapsed]);

    // ── Send message ─────────────────────────────────────────
    const sendMessage = useCallback(async (text) => {
        if (!text.trim() || isLoading) return;

        const userMsg = { role: 'user', content: text.trim() };
        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setIsLoading(true);

        try {
            const res = await api.post('/analytics/chat', {
                question: text.trim(),
                analyticsContext: {
                    viewType: analyticsContext.type || 'overview',
                    viewId: analyticsContext.id || null,
                    viewTitle: analyticsContext.title || null,
                },
                conversationHistory: messages.slice(-10).map(m => ({
                    role: m.role,
                    content: m.content,
                })),
            });

            const aiMsg = {
                role: 'assistant',
                content: res.data.response || res.data.message || 'I couldn\'t generate a response. Please try again.',
                exportOptions: res.data.exportOptions || null,
            };
            setMessages(prev => [...prev, aiMsg]);
        } catch (err) {
            console.error('AI chat error:', err);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: 'Sorry, I encountered an error. Please try again.',
            }]);
        } finally {
            setIsLoading(false);
        }
    }, [isLoading, analyticsContext, messages]);

    // ── Export handler ────────────────────────────────────────
    const handleExport = async (reportType, format) => {
        setIsExporting(true);
        try {
            const res = await api.post('/analytics/report', {
                reportType,
                format,
                analyticsContext: {
                    viewType: analyticsContext.type || 'overview',
                    viewId: analyticsContext.id || null,
                },
            }, { responseType: 'blob' });

            const ext = format === 'csv' ? 'csv' : format === 'json' ? 'json' : format;
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = `analytics_report.${ext}`;
            link.click();
            window.URL.revokeObjectURL(url);
            toast.success('Report exported');
        } catch {
            toast.error('Export failed');
        } finally {
            setIsExporting(false);
        }
    };

    // ── Generate AI Report ─────────────────────────────────────
    const handleGenerateReport = async () => {
        setIsGeneratingReport(true);
        try {
            const res = await api.post('/analytics/report', {
                reportType,
                format: 'ai-report',
                analyticsContext: {
                    viewType: analyticsContext.type || 'overview',
                    viewId: analyticsContext.id || null,
                },
            });

            if (res.data.success && res.data.report) {
                setReportPreview({
                    title: res.data.report.title,
                    markdown: res.data.report.markdown,
                });
                toast.success('Report generated successfully');
            } else {
                toast.error(res.data.message || 'Report generation failed');
            }
        } catch (err) {
            console.error('Report gen error:', err);
            toast.error(err.response?.data?.message || 'Failed to generate report');
        } finally {
            setIsGeneratingReport(false);
        }
    };

    // ── Print report ───────────────────────────────────────────
    const handlePrintReport = () => {
        if (!reportPreview) return;
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <!DOCTYPE html>
            <html><head>
                <title>${reportPreview.title}</title>
                <style>
                    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; max-width: 800px; margin: 40px auto; padding: 0 20px; color: #1a1a1a; line-height: 1.6; }
                    h1 { font-size: 24px; border-bottom: 2px solid #6366f1; padding-bottom: 8px; }
                    h2 { font-size: 18px; color: #374151; margin-top: 28px; }
                    table { width: 100%; border-collapse: collapse; margin: 16px 0; }
                    th, td { border: 1px solid #d1d5db; padding: 8px 12px; text-align: left; font-size: 13px; }
                    th { background: #f3f4f6; font-weight: 600; }
                    ul, ol { padding-left: 24px; }
                    code { background: #f1f5f9; padding: 1px 4px; border-radius: 3px; }
                    hr { border: none; border-top: 1px solid #e5e7eb; margin: 24px 0; }
                    em { color: #6b7280; }
                    @media print { body { margin: 20px; } }
                </style>
            </head><body>${renderMarkdownToHtml(reportPreview.markdown)}</body></html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => printWindow.print(), 500);
    };

    // ── Download markdown as .md file ──────────────────────────
    const handleDownloadMarkdown = () => {
        if (!reportPreview) return;
        const blob = new Blob([reportPreview.markdown], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${reportPreview.title.replace(/\s+/g, '_').toLowerCase()}.md`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Markdown file downloaded');
    };

    // ── Basic markdown → HTML renderer ────────────────────────
    const renderMarkdownToHtml = (md) => {
        if (!md) return '';
        return md
            // Headers
            .replace(/^### (.+)$/gm, '<h3>$1</h3>')
            .replace(/^## (.+)$/gm, '<h2>$1</h2>')
            .replace(/^# (.+)$/gm, '<h1>$1</h1>')
            // Bold & italic
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.+?)\*/g, '<em>$1</em>')
            // Code
            .replace(/`(.+?)`/g, '<code>$1</code>')
            // Horizontal rule
            .replace(/^---$/gm, '<hr/>')
            // Tables
            .replace(/^\|(.+)\|$/gm, (match) => {
                const cells = match.split('|').filter(c => c.trim());
                if (cells.every(c => /^[\s-:]+$/.test(c))) return ''; // separator row
                const isHeader = false; // simplified
                const tag = 'td';
                return '<tr>' + cells.map(c => `<${tag}>${c.trim()}</${tag}>`).join('') + '</tr>';
            })
            // Wrap consecutive table rows
            .replace(/((<tr>.*<\/tr>\n?)+)/g, '<table>$1</table>')
            // Unordered lists
            .replace(/^[\-\*] (.+)$/gm, '<li>$1</li>')
            .replace(/((<li>.*<\/li>\n?)+)/g, '<ul>$1</ul>')
            // Ordered lists
            .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
            // Paragraphs (lines not already tagged)
            .replace(/^(?!<[hultdohr])(\S.+)$/gm, '<p>$1</p>')
            // Newlines
            .replace(/\n/g, '');
    };

    // ── Format AI message (basic markdown) ───────────────────
    const formatMessage = (content) => {
        if (!content) return '';
        let text = typeof content === 'string' ? content : (content.response || content.message || JSON.stringify(content));
        if (typeof text !== 'string') text = String(text);
        return text
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
            .replace(/`(.*?)`/g, '<code style="background:#F1F5F9;padding:1px 4px;border-radius:3px;font-size:12px">$1</code>')
            .replace(/\n/g, '<br/>');
    };

    // ── KEY PRESS ────────────────────────────────────────────
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage(input);
        }
    };

    // ═══════════════════════════════════════════════════════════
    //  REPORT PREVIEW MODAL
    // ═══════════════════════════════════════════════════════════
    if (reportPreview) {
        return (
            <>
                <div
                    onClick={() => setReportPreview(null)}
                    style={{
                        position: 'fixed', inset: 0, zIndex: 10000,
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        animation: 'fadeIn 0.2s ease-out',
                    }}
                />
                <style>{`
                    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                    @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
                `}</style>
                <div style={{
                    position: 'fixed', inset: '3vh 3vw',
                    zIndex: 10001,
                    backgroundColor: TOKENS.bgCard,
                    borderRadius: '16px',
                    boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
                    display: 'flex', flexDirection: 'column',
                    overflow: 'hidden',
                    animation: 'slideUp 0.3s ease-out',
                }}>
                    {/* Header */}
                    <div style={{
                        padding: '16px 24px',
                        background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        flexShrink: 0,
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                                width: '36px', height: '36px', borderRadius: '10px',
                                backgroundColor: 'rgba(255,255,255,0.15)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <FileText size={18} color="white" />
                            </div>
                            <div>
                                <h2 style={{ color: 'white', fontSize: '16px', fontWeight: 700, margin: 0 }}>
                                    {reportPreview.title}
                                </h2>
                                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', margin: '2px 0 0' }}>
                                    AI-Generated Report • Ready to export
                                </p>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                                onClick={handleDownloadMarkdown}
                                style={{
                                    padding: '8px 14px', borderRadius: '8px', border: 'none',
                                    backgroundColor: 'rgba(255,255,255,0.15)', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    color: 'white', fontSize: '12px', fontWeight: 500,
                                    transition: 'background-color 0.2s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.25)'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'}
                                title="Download as Markdown"
                            >
                                <Download size={14} /> .md
                            </button>
                            <button
                                onClick={handlePrintReport}
                                style={{
                                    padding: '8px 14px', borderRadius: '8px', border: 'none',
                                    backgroundColor: 'rgba(255,255,255,0.15)', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    color: 'white', fontSize: '12px', fontWeight: 500,
                                    transition: 'background-color 0.2s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.25)'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'}
                                title="Print / Save as PDF"
                            >
                                <Printer size={14} /> Print / PDF
                            </button>
                            <button
                                onClick={() => setReportPreview(null)}
                                style={{
                                    padding: '8px', borderRadius: '8px', border: 'none',
                                    backgroundColor: 'rgba(255,255,255,0.1)', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    transition: 'background-color 0.2s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                                title="Close"
                            >
                                <X size={18} color="white" />
                            </button>
                        </div>
                    </div>

                    {/* Report content */}
                    <div style={{
                        flex: 1, overflowY: 'auto', padding: '32px 48px',
                        fontSize: '14px', lineHeight: 1.7, color: TOKENS.textPrimary,
                    }}>
                        <div dangerouslySetInnerHTML={{ __html: renderMarkdownToHtml(reportPreview.markdown) }} />
                    </div>
                </div>
            </>
        );
    }

    // ═══════════════════════════════════════════════════════════
    //  COLLAPSED STATE — compact bar
    // ═══════════════════════════════════════════════════════════
    if (isCollapsed && !isFullscreen) {
        return (
            <div
                onClick={() => setIsCollapsed(false)}
                style={{
                    margin: '16px 0 8px',
                    padding: '12px 16px',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #EEF2FF, #F5F3FF)',
                    border: '1px solid #C7D2FE',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    transition: 'all 0.2s',
                }}
                onMouseEnter={e => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(99,102,241,0.15)';
                    e.currentTarget.style.borderColor = '#A5B4FC';
                }}
                onMouseLeave={e => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.borderColor = '#C7D2FE';
                }}
            >
                <div style={{
                    width: '32px', height: '32px', borderRadius: '8px',
                    background: 'linear-gradient(135deg, #6366f1, #818cf8)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <Sparkles size={16} color="white" />
                </div>
                <div style={{ flex: 1 }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: TOKENS.textPrimary }}>
                        Ask AI about this data
                    </span>
                    <p style={{ fontSize: '11px', color: TOKENS.textMuted, margin: '2px 0 0' }}>
                        Get insights, create reports, and explore deeper
                    </p>
                </div>
                <ChevronUp size={16} color={TOKENS.textMuted} />
            </div>
        );
    }

    // ═══════════════════════════════════════════════════════════
    //  RENDER CHAT CONTENT (shared between in-drawer and fullscreen)
    // ═══════════════════════════════════════════════════════════
    const renderChat = (maxHeight) => (
        <div style={{ display: 'flex', flexDirection: 'column', height: maxHeight || '100%' }}>
            {/* Messages area */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '12px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
            }}>
                {messages.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <Sparkles size={28} color={TOKENS.accent} style={{ margin: '0 auto 8px' }} />
                        <p style={{ fontSize: '14px', fontWeight: 600, color: TOKENS.textPrimary, margin: '0 0 4px' }}>
                            AI Analytics Assistant
                        </p>
                        <p style={{ fontSize: '12px', color: TOKENS.textMuted, margin: '0 0 16px' }}>
                            Ask questions about your {analyticsContext.title || analyticsContext.type || 'analytics'} data
                        </p>
                        {/* Quick suggestions */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center' }}>
                            {QUICK_SUGGESTIONS.map((s, i) => (
                                <button
                                    key={i}
                                    onClick={() => sendMessage(s)}
                                    style={{
                                        padding: '6px 12px',
                                        borderRadius: '16px',
                                        border: '1px solid #C7D2FE',
                                        background: '#F5F3FF',
                                        color: TOKENS.accent,
                                        fontSize: '12px',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        transition: 'all 0.15s',
                                    }}
                                    onMouseEnter={e => {
                                        e.currentTarget.style.background = '#EEF2FF';
                                        e.currentTarget.style.borderColor = TOKENS.accent;
                                    }}
                                    onMouseLeave={e => {
                                        e.currentTarget.style.background = '#F5F3FF';
                                        e.currentTarget.style.borderColor = '#C7D2FE';
                                    }}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {messages.map((msg, i) => (
                    <div key={i} style={{
                        display: 'flex',
                        justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    }}>
                        <div style={{
                            maxWidth: '85%',
                            padding: '10px 14px',
                            borderRadius: msg.role === 'user'
                                ? '14px 14px 4px 14px'
                                : '14px 14px 14px 4px',
                            backgroundColor: msg.role === 'user' ? TOKENS.accent : TOKENS.bgPage,
                            color: msg.role === 'user' ? 'white' : TOKENS.textPrimary,
                            fontSize: '13px',
                            lineHeight: 1.5,
                            border: msg.role === 'user' ? 'none' : `1px solid ${TOKENS.borderLight}`,
                        }}>
                            <div dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }} />
                            {/* Export buttons from AI */}
                            {msg.exportOptions && (
                                <div style={{
                                    display: 'flex', gap: '6px', marginTop: '8px',
                                    borderTop: `1px solid ${msg.role === 'user' ? 'rgba(255,255,255,0.2)' : TOKENS.borderLight}`,
                                    paddingTop: '8px',
                                }}>
                                    <button
                                        onClick={() => handleExport('default', 'csv')}
                                        disabled={isExporting}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '4px',
                                            padding: '4px 10px', borderRadius: '6px',
                                            border: `1px solid ${TOKENS.border}`,
                                            background: TOKENS.bgCard, fontSize: '11px',
                                            fontWeight: 500, cursor: 'pointer', color: TOKENS.textSecondary,
                                        }}
                                    >
                                        <FileSpreadsheet size={12} /> CSV
                                    </button>
                                    <button
                                        onClick={() => handleExport('default', 'json')}
                                        disabled={isExporting}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '4px',
                                            padding: '4px 10px', borderRadius: '6px',
                                            border: `1px solid ${TOKENS.border}`,
                                            background: TOKENS.bgCard, fontSize: '11px',
                                            fontWeight: 500, cursor: 'pointer', color: TOKENS.textSecondary,
                                        }}
                                    >
                                        <FileText size={12} /> JSON
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                ))}

                {isLoading && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0' }}>
                        <div style={{
                            padding: '10px 14px', borderRadius: '14px 14px 14px 4px',
                            backgroundColor: TOKENS.bgPage, border: `1px solid ${TOKENS.borderLight}`,
                            display: 'flex', alignItems: 'center', gap: '8px',
                        }}>
                            <Loader2 size={14} color={TOKENS.accent} style={{ animation: 'spin 1s linear infinite' }} />
                            <span style={{ fontSize: '13px', color: TOKENS.textMuted }}>Analyzing...</span>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input area */}
            <div style={{
                padding: '10px 14px',
                borderTop: `1px solid ${TOKENS.borderLight}`,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: TOKENS.bgCard,
            }}>
                <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask a question about the data..."
                    style={{
                        flex: 1,
                        padding: '10px 14px',
                        borderRadius: '10px',
                        border: `1px solid ${TOKENS.border}`,
                        fontSize: '13px',
                        outline: 'none',
                        backgroundColor: TOKENS.bgPage,
                        color: TOKENS.textPrimary,
                        transition: 'border-color 0.2s',
                    }}
                    onFocus={e => e.currentTarget.style.borderColor = TOKENS.accent}
                    onBlur={e => e.currentTarget.style.borderColor = TOKENS.border}
                    disabled={isLoading}
                />
                <button
                    onClick={() => sendMessage(input)}
                    disabled={!input.trim() || isLoading}
                    style={{
                        width: '38px', height: '38px',
                        borderRadius: '10px', border: 'none',
                        background: input.trim() && !isLoading
                            ? 'linear-gradient(135deg, #6366f1, #818cf8)'
                            : TOKENS.bgHover,
                        color: input.trim() && !isLoading ? 'white' : TOKENS.textMuted,
                        cursor: input.trim() && !isLoading ? 'pointer' : 'default',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                        transition: 'all 0.2s',
                    }}
                >
                    <Send size={16} />
                </button>
            </div>

            {/* Report Generator Section */}
            <div style={{
                padding: '10px 14px',
                borderTop: `1px solid ${TOKENS.borderLight}`,
                backgroundColor: '#FAFBFF',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <FileType size={14} color={TOKENS.accent} />
                    <span style={{ fontSize: '12px', fontWeight: 600, color: TOKENS.textPrimary }}>Generate AI Report</span>
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                    <select
                        value={reportType}
                        onChange={e => setReportType(e.target.value)}
                        style={{
                            flex: 1, padding: '7px 10px', borderRadius: '8px',
                            border: `1px solid ${TOKENS.border}`, fontSize: '12px',
                            backgroundColor: TOKENS.bgCard, color: TOKENS.textPrimary,
                            outline: 'none', cursor: 'pointer',
                        }}
                    >
                        <option value="comprehensive">Comprehensive</option>
                        <option value="employees">Employee Performance</option>
                        <option value="tasks">Task Analytics</option>
                        <option value="compliance">Compliance</option>
                        <option value="onboarding">Onboarding</option>
                        <option value="preboarding">Preboarding</option>
                        <option value="surveys">Surveys</option>
                        <option value="notices">Notices</option>
                    </select>
                    <button
                        onClick={handleGenerateReport}
                        disabled={isGeneratingReport}
                        style={{
                            padding: '7px 14px', borderRadius: '8px', border: 'none',
                            background: isGeneratingReport ? TOKENS.bgHover : 'linear-gradient(135deg, #6366f1, #818cf8)',
                            color: isGeneratingReport ? TOKENS.textMuted : 'white',
                            fontSize: '12px', fontWeight: 600, cursor: isGeneratingReport ? 'default' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: '5px',
                            transition: 'all 0.2s', whiteSpace: 'nowrap',
                        }}
                    >
                        {isGeneratingReport ? (
                            <><Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> Generating...</>
                        ) : (
                            <><Sparkles size={12} /> Generate</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );

    // ═══════════════════════════════════════════════════════════
    //  FULLSCREEN MODE
    // ═══════════════════════════════════════════════════════════
    if (isFullscreen) {
        return (
            <div style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                animation: 'fadeIn 0.2s ease-out',
            }}>
                <style>{`
                    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                    @keyframes spin { to { transform: rotate(360deg); } }
                `}</style>
                <div style={{
                    width: '90vw', maxWidth: '1200px',
                    height: '85vh',
                    backgroundColor: TOKENS.bgCard,
                    borderRadius: '16px',
                    boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
                    display: 'flex', flexDirection: 'column',
                    overflow: 'hidden',
                }}>
                    {/* Fullscreen header */}
                    <div style={{
                        padding: '16px 20px',
                        background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        flexShrink: 0,
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                                width: '36px', height: '36px', borderRadius: '10px',
                                backgroundColor: 'rgba(255,255,255,0.15)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <Sparkles size={18} color="white" />
                            </div>
                            <div>
                                <h2 style={{ color: 'white', fontSize: '16px', fontWeight: 700, margin: 0 }}>
                                    AI Analytics — {analyticsContext.title || analyticsContext.type || 'Overview'}
                                </h2>
                                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', margin: '2px 0 0' }}>
                                    Full analysis mode • {messages.length} messages
                                </p>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                                onClick={() => setIsFullscreen(false)}
                                style={{
                                    padding: '8px', borderRadius: '8px', border: 'none',
                                    backgroundColor: 'rgba(255,255,255,0.1)', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    transition: 'background-color 0.2s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                                title="Minimize"
                            >
                                <Minimize2 size={18} color="white" />
                            </button>
                            <button
                                onClick={() => { setIsFullscreen(false); setIsCollapsed(true); }}
                                style={{
                                    padding: '8px', borderRadius: '8px', border: 'none',
                                    backgroundColor: 'rgba(255,255,255,0.1)', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    transition: 'background-color 0.2s',
                                }}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                                title="Close"
                            >
                                <X size={18} color="white" />
                            </button>
                        </div>
                    </div>

                    {/* Fullscreen chat */}
                    {renderChat('100%')}
                </div>
            </div>
        );
    }

    // ═══════════════════════════════════════════════════════════
    //  IN-DRAWER EXPANDED STATE
    // ═══════════════════════════════════════════════════════════
    return (
        <div style={{
            margin: '16px 0 8px',
            borderRadius: '12px',
            border: '1px solid #C7D2FE',
            overflow: 'hidden',
            backgroundColor: TOKENS.bgCard,
            boxShadow: '0 2px 8px rgba(99,102,241,0.08)',
        }}>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            {/* Header */}
            <div style={{
                padding: '10px 14px',
                background: 'linear-gradient(135deg, #EEF2FF, #F5F3FF)',
                borderBottom: '1px solid #C7D2FE',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                        width: '28px', height: '28px', borderRadius: '7px',
                        background: 'linear-gradient(135deg, #6366f1, #818cf8)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Sparkles size={14} color="white" />
                    </div>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: TOKENS.textPrimary }}>
                        AI Assistant
                    </span>
                    {messages.length > 0 && (
                        <span style={{
                            fontSize: '11px', color: TOKENS.textMuted,
                            backgroundColor: TOKENS.bgPage, padding: '1px 6px',
                            borderRadius: '8px',
                        }}>
                            {messages.length} msgs
                        </span>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                        onClick={() => setIsFullscreen(true)}
                        style={{
                            padding: '5px', borderRadius: '6px', border: 'none',
                            backgroundColor: 'transparent', cursor: 'pointer',
                            display: 'flex', alignItems: 'center',
                            color: TOKENS.textMuted, transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#E0E7FF'; e.currentTarget.style.color = TOKENS.accent; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = TOKENS.textMuted; }}
                        title="Expand to fullscreen"
                    >
                        <Maximize2 size={14} />
                    </button>
                    <button
                        onClick={() => setIsCollapsed(true)}
                        style={{
                            padding: '5px', borderRadius: '6px', border: 'none',
                            backgroundColor: 'transparent', cursor: 'pointer',
                            display: 'flex', alignItems: 'center',
                            color: TOKENS.textMuted, transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#E0E7FF'; e.currentTarget.style.color = TOKENS.accent; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = TOKENS.textMuted; }}
                        title="Collapse"
                    >
                        <ChevronDown size={14} />
                    </button>
                    <button
                        onClick={() => setIsCollapsed(true)}
                        style={{
                            padding: '5px', borderRadius: '6px', border: 'none',
                            backgroundColor: 'transparent', cursor: 'pointer',
                            display: 'flex', alignItems: 'center',
                            color: TOKENS.textMuted, transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#FEE2E2'; e.currentTarget.style.color = '#EF4444'; }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = TOKENS.textMuted; }}
                        title="Close"
                    >
                        <X size={14} />
                    </button>
                </div>
            </div>

            {/* Chat body */}
            {renderChat('320px')}
        </div>
    );
};

export default AnalyticsAIPanel;
